import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Skeleton } from '@/components/ui/skeleton';
import { getLegalPage } from '@/lib/api';
import { ChevronLeft } from 'lucide-react';

export const LegalPage = () => {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getLegalPage(slug);
        setPage(res.data);
      } catch {
        setPage(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const lang = i18n.language?.startsWith('de') ? 'de' : 'en';
  const title = page ? (page[`title_${lang}`] || page.title_de) : '';
  const content = page ? (page[`content_${lang}`] || page.content_de) : '';

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="legal-page">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="legal-back-link">
            <ChevronLeft className="h-4 w-4" />
            Zurück zur Startseite
          </Link>

          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : page ? (
            <article>
              <h1 className="font-manrope text-3xl font-bold mb-8" data-testid="legal-page-title">{title}</h1>
              <div
                className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-manrope prose-h2:text-2xl prose-h3:text-lg prose-p:text-sm prose-li:text-sm prose-a:text-orange-600 hover:prose-a:text-orange-700"
                dangerouslySetInnerHTML={{ __html: content }}
                data-testid="legal-page-content"
              />
              {page.updated_at && (
                <p className="mt-10 text-xs text-muted-foreground border-t pt-4">
                  Zuletzt aktualisiert: {new Date(page.updated_at).toLocaleDateString('de-DE')}
                  {page.updated_by && page.updated_by !== 'system' && ` von ${page.updated_by}`}
                </p>
              )}
            </article>
          ) : (
            <div className="text-center py-16">
              <h2 className="text-xl font-semibold mb-2">Seite nicht gefunden</h2>
              <p className="text-muted-foreground">Die angeforderte Seite existiert nicht.</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
